/**
 * Buyer auth client — NayaDesk `/api/advisor/auth/*`.
 *
 * The identifier is a sign-in key, not a lead: Desk stores it in tables
 * that never join to leads/conversations, and the only promised use is
 * sending the sign-in code. See NayaDesk src/api/advisor_auth.ts.
 *
 * Base URL: VITE_DESK_API_BASE (falls back to the dev Desk worker so the
 * dev Pages deploy works without extra env plumbing; prod sets the var).
 */

const DESK_BASE =
  (import.meta.env.VITE_DESK_API_BASE as string | undefined) ??
  'https://nayadesk-dev.nagarjun-arjun.workers.dev/api';

const AUTH_BASE = `${DESK_BASE.replace(/\/$/, '')}/advisor/auth`;

export type BuyerMethod = 'phone' | 'email';

export interface BuyerInfo {
  method: BuyerMethod;
  masked: string;
  created_at: number;
}

export interface StartResponse {
  status?: 'ok';
  error?: string;
  retry_after_s?: number;
  challenge_id?: string;
  masked?: string;
  expires_in_s?: number;
  delivery?: 'dev_echo' | 'none';
  /** Present only when the Desk dev worker echoes codes (no provider wired). */
  dev_code?: string;
}

export interface VerifyResponse {
  status?: 'ok';
  error?: string;
  token?: string;
  expires_at?: number;
  buyer?: BuyerInfo;
  state?: string;
  state_saved_at?: number;
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const res = await fetch(`${AUTH_BASE}${path}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  return (await res.json().catch(() => ({ error: `http_${res.status}` }))) as T;
}

export function startSignIn(method: BuyerMethod, identifier: string): Promise<StartResponse> {
  return post<StartResponse>('/start', { method, identifier });
}

export function verifyCode(challengeId: string, code: string): Promise<VerifyResponse> {
  return post<VerifyResponse>('/verify', { challenge_id: challengeId, code });
}

export async function fetchMe(
  token: string,
): Promise<{ status?: 'ok'; error?: string; buyer?: BuyerInfo; state_saved_at?: number }> {
  const res = await fetch(`${AUTH_BASE}/me`, {
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json().catch(() => ({ error: `http_${res.status}` }))) as {
    status?: 'ok';
    error?: string;
    buyer?: BuyerInfo;
    state_saved_at?: number;
  };
}

export async function fetchState(
  token: string,
): Promise<{ status?: 'ok'; error?: string; state?: string | null; saved_at?: number }> {
  const res = await fetch(`${AUTH_BASE}/state`, {
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json().catch(() => ({ error: `http_${res.status}` }))) as {
    status?: 'ok';
    error?: string;
    state?: string | null;
    saved_at?: number;
  };
}

export async function pushState(
  token: string,
  state: string,
  savedAt: number,
): Promise<{ ok?: boolean; error?: string }> {
  const res = await fetch(`${AUTH_BASE}/state`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ state, saved_at: savedAt }),
  });
  return (await res.json().catch(() => ({ error: `http_${res.status}` }))) as {
    ok?: boolean;
    error?: string;
  };
}

export function signOut(token: string): Promise<{ ok?: boolean }> {
  return post<{ ok?: boolean }>('/signout', {}, token);
}

export async function deleteProfile(
  token: string,
): Promise<{ status?: 'ok'; error?: string; erased?: boolean }> {
  const res = await fetch(`${AUTH_BASE}/profile`, {
    method: 'DELETE',
    headers: { authorization: `Bearer ${token}` },
  });
  return (await res.json().catch(() => ({ error: `http_${res.status}` }))) as {
    status?: 'ok';
    error?: string;
    erased?: boolean;
  };
}
